import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Scale, ArrowLeft, Clock, FileText, CheckSquare, DollarSign, MessageCircle, Calendar, Users } from 'lucide-react';

const LexyDemo = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div className="min-h-screen bg-[#F4F2EE]">
      
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button type="button" onClick={() => navigate('/lawyer')} className="w-9 h-9 flex items-center justify-center hover:bg-slate-100 rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-purple-600 rounded-xl flex items-center justify-center">
                <Scale className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-slate-900">Lexy Workspace</h1>
                <p className="text-xs text-slate-600">Depositum-sak - Martin S.</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-full text-xs font-bold">
              LEXY PRO
            </div>
            <div className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">
              PÅGÅENDE
            </div>
          </div>
        </div>
      </header>

      {/* Main Layout */}
      <div className="flex h-[calc(100vh-73px)]">
        
        {/* Sidebar */}
        <div className="w-64 bg-white border-r border-slate-200 p-4">
          <nav className="space-y-1">
            {[
              { id: 'overview', icon: Scale, label: 'Oversikt' },
              { id: 'chat', icon: MessageCircle, label: 'Chat med klient' },
              { id: 'documents', icon: FileText, label: 'Dokumenter' },
              { id: 'time', icon: Clock, label: 'Timer' },
              { id: 'tasks', icon: CheckSquare, label: 'Oppgaver' },
              { id: 'billing', icon: DollarSign, label: 'Fakturering' },
              { id: 'calendar', icon: Calendar, label: 'Kalender' },
              { id: 'team', icon: Users, label: 'Team' }
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  activeTab === item.id
                    ? 'bg-purple-100 text-purple-700 font-semibold'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto p-8">
          
          {activeTab === 'overview' && (
            <div className="max-w-5xl mx-auto space-y-6">
              <h2 className="text-3xl font-bold text-slate-900 mb-8">Saks-oversikt</h2>
              
              {/* Stats */}
              <div className="grid grid-cols-4 gap-6">
                <div className="bg-white border border-slate-200 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Clock className="w-5 h-5 text-blue-600" />
                    </div>
                    <span className="text-sm text-slate-600">Timer</span>
                  </div>
                  <div className="text-3xl font-bold text-slate-900">8.5t</div>
                  <p className="text-xs text-slate-500 mt-1">av 10-12t estimert</p>
                </div>
                
                <div className="bg-white border border-slate-200 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-emerald-600" />
                    </div>
                    <span className="text-sm text-slate-600">Fakturert</span>
                  </div>
                  <div className="text-3xl font-bold text-slate-900">12.750 kr</div>
                  <p className="text-xs text-slate-500 mt-1">8.5t × 1.500 kr</p>
                </div>
                
                <div className="bg-white border border-slate-200 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                      <CheckSquare className="w-5 h-5 text-purple-600" />
                    </div>
                    <span className="text-sm text-slate-600">Oppgaver</span>
                  </div>
                  <div className="text-3xl font-bold text-slate-900">3/7</div>
                  <p className="text-xs text-slate-500 mt-1">ferdigstilt</p>
                </div>
                
                <div className="bg-white border border-slate-200 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                      <FileText className="w-5 h-5 text-orange-600" />
                    </div>
                    <span className="text-sm text-slate-600">Dokumenter</span>
                  </div>
                  <div className="text-3xl font-bold text-slate-900">12</div>
                  <p className="text-xs text-slate-500 mt-1">opplastet</p>
                </div>
              </div>

              {/* Timeline */}
              <div className="bg-white border border-slate-200 rounded-2xl p-8">
                <h3 className="font-bold text-slate-900 mb-6 text-xl">Tidsline</h3>
                <div className="space-y-4">
                  {[
                    { time: 'I dag 14:30', event: 'Du registrerte 2.5 timer', icon: Clock },
                    { time: 'I dag 12:15', event: 'Klient lastet opp kontrakt.pdf', icon: FileText },
                    { time: 'I går 16:20', event: 'Du fullførte oppgave: Gjennomgå dokumenter', icon: CheckSquare },
                    { time: '15. nov', event: 'Pristilbud akseptert av klient', icon: DollarSign }
                  ].map((item, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-5 h-5 text-slate-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{item.event}</p>
                        <p className="text-sm text-slate-500">{item.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'chat' && (
            <div className="max-w-4xl mx-auto">
              <div className="bg-white border border-slate-200 rounded-2xl p-8">
                <h3 className="font-bold text-slate-900 mb-6 text-xl">Chat med Martin S.</h3>
                <div className="space-y-4 mb-6">
                  <div className="flex gap-3">
                    <div className="w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center">
                      <Scale className="w-5 h-5 text-white" />
                    </div>
                    <div className="bg-slate-100 px-4 py-3 rounded-2xl rounded-tl-sm max-w-md">
                      <p className="text-sm text-slate-800">
                        Jeg har gjennomgått dokumentene. Ser ut som vi har god sak. Skal jeg starte med stev-utkast?
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3 justify-end">
                    <div className="bg-slate-900 text-white px-4 py-3 rounded-2xl rounded-tr-sm max-w-md">
                      <p className="text-sm">
                        Ja, gjerne! Når kan vi regne med utkast?
                      </p>
                    </div>
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </div>
                <div className="border-t border-slate-200 pt-4">
                  <input
                    placeholder="Skriv melding..."
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl"
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'time' && (
            <div className="max-w-5xl mx-auto">
              <div className="bg-white border border-slate-200 rounded-2xl p-8">
                <h3 className="font-bold text-slate-900 mb-6 text-xl">Timeregistrering</h3>
                <div className="space-y-3">
                  {[
                    { date: '16. nov', hours: 2.5, description: 'Gjennomgang av dokumenter', billable: true },
                    { date: '15. nov', hours: 3.0, description: 'Juridisk analyse', billable: true },
                    { date: '14. nov', hours: 1.5, description: 'Korrespondanse med klient', billable: true },
                    { date: '13. nov', hours: 1.5, description: 'Research - lovdata', billable: true }
                  ].map((entry, i) => (
                    <div key={i} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{entry.description}</p>
                        <p className="text-sm text-slate-500">{entry.date}</p>
                      </div>
                      <div className="flex items-center gap-6">
                        <span className="font-bold text-slate-900">{entry.hours}t</span>
                        <span className="text-slate-600">{entry.hours * 1500} kr</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                          entry.billable ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'
                        }`}>
                          {entry.billable ? 'Fakturerbar' : 'Intern'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-6 border-t border-slate-200 flex justify-between items-center">
                  <span className="font-bold text-slate-900">Total:</span>
                  <span className="text-2xl font-bold text-slate-900">8.5t = 12.750 kr</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'tasks' && (
            <div className="max-w-5xl mx-auto">
              <div className="bg-white border border-slate-200 rounded-2xl p-8">
                <h3 className="font-bold text-slate-900 mb-6 text-xl">Oppgaver</h3>
                <div className="space-y-3">
                  {[
                    { task: 'Gjennomgå kontrakt', done: true, due: '15. nov' },
                    { task: 'Send stev-utkast til klient', done: true, due: '16. nov' },
                    { task: 'Innhent tilleggsopplysninger', done: true, due: '17. nov' },
                    { task: 'Forbered møte med motpart', done: false, due: '18. nov' },
                    { task: 'Utarbeide forlik-forslag', done: false, due: '20. nov' },
                    { task: 'Frist: Sende stev', done: false, due: '25. nov', urgent: true }
                  ].map((item, i) => (
                    <div key={i} className={`flex items-center gap-4 p-4 rounded-xl ${
                      item.done ? 'bg-emerald-50 border border-emerald-200' : item.urgent ? 'bg-red-50 border border-red-200' : 'bg-slate-50 border border-slate-200'
                    }`}>
                      <input type="checkbox" checked={item.done} className="w-5 h-5" readOnly />
                      <div className="flex-1">
                        <p className={`font-medium ${item.done ? 'text-slate-500 line-through' : 'text-slate-900'}`}>
                          {item.task}
                        </p>
                        <p className="text-sm text-slate-500">Frist: {item.due}</p>
                      </div>
                      {item.urgent && (
                        <span className="px-2 py-1 bg-red-600 text-white rounded-full text-xs font-bold">
                          VIKTIG
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="max-w-5xl mx-auto">
              <div className="bg-white border border-slate-200 rounded-2xl p-8">
                <h3 className="font-bold text-slate-900 mb-6 text-xl">Dokumenter</h3>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    'Leieavtale.pdf',
                    'Depositumkvittering.pdf',
                    'Korrespondanse.pdf',
                    'Flytteprotokoll.pdf',
                    'Bilder_leilighet.zip',
                    'Stev_utkast_v1.docx'
                  ].map((doc, i) => (
                    <div key={i} className="border border-slate-200 rounded-xl p-4 hover:border-slate-300 cursor-pointer transition-all">
                      <FileText className="w-8 h-8 text-slate-400 mb-2" />
                      <p className="text-sm font-medium text-slate-900 truncate">{doc}</p>
                      <p className="text-xs text-slate-500">16. nov 2025</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'billing' && (
            <div className="max-w-5xl mx-auto">
              <div className="bg-white border border-slate-200 rounded-2xl p-8">
                <h3 className="font-bold text-slate-900 mb-6 text-xl">Fakturering</h3>
                <div className="space-y-6">
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
                    <div className="flex justify-between items-center mb-4">
                      <span className="font-semibold text-slate-900">Pristilbud</span>
                      <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold">
                        GODKJENT
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-slate-600">Estimert:</span>
                        <p className="font-bold text-slate-900">10-12 timer</p>
                      </div>
                      <div>
                        <span className="text-slate-600">Timepris:</span>
                        <p className="font-bold text-slate-900">1.500 kr</p>
                      </div>
                      <div>
                        <span className="text-slate-600">Total estimat:</span>
                        <p className="font-bold text-slate-900">15.000-18.000 kr</p>
                      </div>
                      <div>
                        <span className="text-slate-600">Faktisk så langt:</span>
                        <p className="font-bold text-emerald-600">12.750 kr</p>
                      </div>
                    </div>
                  </div>
                  
                  <Button className="w-full bg-slate-900 hover:bg-slate-800 py-6 text-lg font-bold rounded-xl">
                    Generer faktura
                  </Button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Demo Note */}
      <div className="fixed bottom-6 right-6 bg-purple-600 text-white px-6 py-3 rounded-full shadow-lg">
        <p className="text-sm font-bold">✨ Lexy Workspace Demo</p>
      </div>
    </div>
  );
};

export default LexyDemo;
